import React, { useState } from 'react';
import { CreatePage } from '../../../components/crud/CreatePage';
import { Card, CardContent } from '../../../components/ui/card';
import { Input } from '../../../components/ui/input';
import { Textarea } from '../../../components/ui/textarea';
import { Label } from '../../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Switch } from '../../../components/ui/switch';
import { Button } from '../../../components/ui/button';
import { useToast } from '../../../components/ui/use-toast';
import { useCreate, useSelect } from '@refinedev/core';
import { useNavigate } from 'react-router-dom';

export const MODALITIES = [
  { value: 'DX', label: 'Digital Radiography (X-Ray)' },
  { value: 'CR', label: 'Computed Radiography' },
  { value: 'US', label: 'Ultrasound' },
  { value: 'RF', label: 'Radio Fluoroscopy' },
  { value: 'XA', label: 'X-Ray Angiography' },
  { value: 'ES', label: 'Endoscopy' },
  { value: 'ECG', label: 'Electrocardiography' },
  { value: 'OP', label: 'Ophthalmic Photography' },
  { value: 'CT', label: 'Computed Tomography' },
  { value: 'MR', label: 'Magnetic Resonance' },
  { value: 'OT', label: 'Other' },
];
export const CONNECTION_TYPES = [
  { value: 'dicom', label: 'DICOM (TCP/IP)' },
  { value: 'video', label: 'Video / Frame Grabber' },
  { value: 'manual', label: 'Manual / Offline' },
];

export const ImagingDeviceCreate: React.FC = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const { mutate, isLoading } = useCreate();
  const { options: connectorOptions } = useSelect({ resource: 'devices/connectors', optionLabel: 'name', optionValue: 'id' });

  const [values, setValues] = useState({
    name: '', manufacturer: '', model: '', serial_number: '', modality: '', category: '',
    connector_device: '', connection_type: 'dicom', dicom_enabled: true,
    device_ip: '', device_port: '104', ae_title: '', status: 'active',
    description: '', qc_enabled: false, qc_frequency_hours: '168',
    calibration_due_date: '', maintenance_due_date: '',
  });
  const set = (f: string, v: any) => setValues(p => ({ ...p, [f]: v }));

  const handleSave = () => {
    if (!values.name || !values.serial_number || !values.modality) {
      toast({ variant: 'destructive', title: 'Name, serial number and modality are required' }); return;
    }
    if (!values.connector_device) {
      toast({ variant: 'destructive', title: 'Connector device is required' }); return;
    }
    const connection_config: Record<string, any> = {};
    if (values.description) connection_config.description = values.description;

    mutate({ resource: 'devices/imaging', values: {
      name: values.name, manufacturer: values.manufacturer, model: values.model,
      serial_number: values.serial_number, modality: values.modality, category: values.category,
      connector_device: values.connector_device, connection_type: values.connection_type,
      dicom_enabled: values.dicom_enabled, status: values.status,
      device_ip: values.device_ip || undefined,
      device_port: values.device_port ? Number(values.device_port) : undefined,
      ae_title: values.ae_title, connection_config,
      qc_enabled: values.qc_enabled, qc_frequency_hours: Number(values.qc_frequency_hours),
      calibration_due_date: values.calibration_due_date || undefined,
      maintenance_due_date: values.maintenance_due_date || undefined,
    }}, {
      onSuccess: () => { toast({ title: 'Imaging device created' }); navigate('/devices/imaging'); },
      onError: (e: any) => toast({ variant: 'destructive', title: 'Failed to create imaging device', description: e?.message }),
    });
  };

  return (
    <CreatePage title="Create Imaging Device" footerButtons={<Button onClick={handleSave} disabled={isLoading}>{isLoading ? 'Saving...' : 'Save'}</Button>}>
      <div className="space-y-6">
        <Card><CardContent className="pt-6 space-y-4">
          <h4 className="text-sm font-semibold text-primary">Basic Information</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1.5"><Label>Device Name <span className="text-destructive">*</span></Label><Input value={values.name} onChange={e => set('name', e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Modality <span className="text-destructive">*</span></Label>
              <Select value={values.modality} onValueChange={v => set('modality', v)}><SelectTrigger><SelectValue placeholder="Select modality" /></SelectTrigger>
                <SelectContent>{MODALITIES.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}</SelectContent></Select></div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-1.5"><Label>Manufacturer</Label><Input value={values.manufacturer} onChange={e => set('manufacturer', e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Model</Label><Input value={values.model} onChange={e => set('model', e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Serial Number <span className="text-destructive">*</span></Label><Input value={values.serial_number} onChange={e => set('serial_number', e.target.value)} /></div>
          </div>
          <div className="space-y-1.5"><Label>Category (label)</Label><Input placeholder="e.g. UltraSound, X-Ray, ECG" value={values.category} onChange={e => set('category', e.target.value)} /></div>
          <div className="space-y-1.5"><Label>Description</Label><Textarea rows={2} value={values.description} onChange={e => set('description', e.target.value)} /></div>
        </CardContent></Card>

        <Card><CardContent className="pt-6 space-y-4">
          <h4 className="text-sm font-semibold text-primary">Connector & Connection</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1.5"><Label>Connector Device <span className="text-destructive">*</span></Label>
              <Select value={values.connector_device} onValueChange={v => set('connector_device', v)}><SelectTrigger><SelectValue placeholder="Select connector" /></SelectTrigger>
                <SelectContent>{(connectorOptions || []).map((o: any) => <SelectItem key={o.value} value={String(o.value)}>{o.label}</SelectItem>)}</SelectContent></Select></div>
            <div className="space-y-1.5"><Label>Connection Type</Label>
              <Select value={values.connection_type} onValueChange={v => set('connection_type', v)}><SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{CONNECTION_TYPES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent></Select></div>
          </div>
        </CardContent></Card>

        <Card><CardContent className="pt-6 space-y-4">
          <h4 className="text-sm font-semibold text-primary">DICOM Configuration</h4>
          <div className="flex items-center gap-2"><Switch checked={values.dicom_enabled} onCheckedChange={v => set('dicom_enabled', v)} /><Label>DICOM-enabled (sends to connector SCP)</Label></div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-1.5"><Label>Calling AE Title</Label><Input maxLength={16} placeholder="US_EDAN_LX9" value={values.ae_title} onChange={e => set('ae_title', e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Device IP</Label><Input placeholder="192.168.0.181" value={values.device_ip} onChange={e => set('device_ip', e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Device Port</Label><Input type="number" min={1} max={65535} placeholder="104" value={values.device_port} onChange={e => set('device_port', e.target.value)} /></div>
          </div>
        </CardContent></Card>

        <Card><CardContent className="pt-6 space-y-4">
          <h4 className="text-sm font-semibold text-primary">Status & Maintenance</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-1.5"><Label>Status</Label>
              <Select value={values.status} onValueChange={v => set('status', v)}><SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="active">Active</SelectItem><SelectItem value="inactive">Inactive</SelectItem><SelectItem value="maintenance">Maintenance</SelectItem></SelectContent></Select></div>
            <div className="flex items-center gap-2 pt-6"><Switch checked={values.qc_enabled} onCheckedChange={v => set('qc_enabled', v)} /><Label>QC Enabled</Label></div>
            <div className="space-y-1.5"><Label>QC Frequency (Hours)</Label><Input type="number" min={1} value={values.qc_frequency_hours} onChange={e => set('qc_frequency_hours', e.target.value)} /></div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1.5"><Label>Calibration Due Date</Label><Input type="date" value={values.calibration_due_date} onChange={e => set('calibration_due_date', e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Maintenance Due Date</Label><Input type="date" value={values.maintenance_due_date} onChange={e => set('maintenance_due_date', e.target.value)} /></div>
          </div>
        </CardContent></Card>
      </div>
    </CreatePage>
  );
};

export default ImagingDeviceCreate;
